<!--
	SessionMap.svelte — Leaflet map showing grid-bucketed session locations.

	Renders circle markers sized proportionally to session count per bucket.
	Uses CartoDB Positron (light) or DarkMatter (dark) tiles depending on theme.

	@prop {import('../AppTypes').SessionLocationBucket[]} buckets — Location data.
	@prop {number} totalSessions — Total sessions in the time window.
-->
<script lang="ts">
	import { run } from 'svelte/legacy';

	import { onMount, onDestroy } from 'svelte';
	import type { SessionLocationBucket } from '../AppTypes';
	import { t, t2 } from '../i18n';

	
	
	interface Props {
		/** Grid-bucketed session location data. */
		buckets?: SessionLocationBucket[];
		/** Total session count for the time window (shown in legend). */
		totalSessions?: number;
	}

	let { buckets = [], totalSessions = 0 }: Props = $props();

	let mapContainer: HTMLDivElement = $state(undefined!);
	let map: any = $state(null);
	let markersLayer: any = $state(null);
	let tileLayer: any = null;
	let themeObserver: MutationObserver | null = null;

	/** Minimum circle radius in pixels. */
	const MIN_RADIUS = 6;
	/** Scale factor for sqrt-based radius. */
	const RADIUS_K = 3;

	/** CartoDB Positron (light) tile URL. */
	const TILES_LIGHT = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
	/** CartoDB DarkMatter (dark) tile URL. */
	const TILES_DARK = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';

	/** Detect whether the app is currently in dark mode. */
	function isDarkMode(): boolean {
		return document.documentElement.classList.contains('dark');
	}

	/** Calculate marker radius from session count. */
	function markerRadius(count: number): number {
		return Math.max(MIN_RADIUS, Math.sqrt(count) * RADIUS_K);
	}

	/**
	 * Swap tile layer to match the current theme (light/dark).
	 * Called on init and whenever the `dark` class toggles on <html>.
	 */
	function applyTileTheme(L: any): void {
		if (!map) return;
		const url = isDarkMode() ? TILES_DARK : TILES_LIGHT;
		if (tileLayer) {
			tileLayer.setUrl(url);
		} else {
			tileLayer = L.tileLayer(url, { subdomains: 'abcd', maxZoom: 19 }).addTo(map);
		}
	}

	/**
	 * Initialise the Leaflet map. Dynamically imports leaflet to avoid
	 * SSR issues and to tree-shake unused parts.
	 */
	async function initMap(): Promise<void> {
		const L = await import('leaflet');
		await import('leaflet/dist/leaflet.css');

		map = L.map(mapContainer, {
			center: [20, 0],
			zoom: 2,
			minZoom: 2,
			maxZoom: 10,
			scrollWheelZoom: true,
			attributionControl: false
		});

		/** Apply theme-aware tiles. */
		applyTileTheme(L);

		/** Small attribution in bottom-right. */
		L.control
			.attribution({ position: 'bottomright', prefix: false })
			.addAttribution('&copy; <a href="https://carto.com">CARTO</a>')
			.addTo(map);

		markersLayer = L.layerGroup().addTo(map);

		renderMarkers(L);

		/**
		 * Watch for dark-mode toggling on <html> and swap tile layer.
		 * The app sets/removes the `dark` class on documentElement.
		 */
		themeObserver = new MutationObserver(() => {
			applyTileTheme(L);
		});
		themeObserver.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['class']
		});
	}

	/** (Re-)render circle markers from the current bucket data. */
	function renderMarkers(L: any): void {
		if (!markersLayer) return;
		markersLayer.clearLayers();

		/** Skip rendering when there are no buckets to display. */
		if (buckets.length === 0) return;

		/** Collect bounds and create circle markers for each bucket. */
		const bounds: [number, number][] = [];

		/** Iterate over each bucket to place a circle marker on the map. */
		for (const bucket of buckets) {
			const latlng: [number, number] = [bucket.lat, bucket.lng];
			bounds.push(latlng);

			const marker = L.circleMarker(latlng, {
				radius: markerRadius(bucket.count),
				fillColor: '#3b82f6',
				color: '#1e40af',
				weight: 1.5,
				fillOpacity: 0.65
			});

			const plural = bucket.count === 1 ? t2('session') : t2('sessions');
			marker.bindTooltip(`<strong>${bucket.count} ${plural}</strong><br/>${bucket.country}`, {
				direction: 'top',
				offset: [0, -6]
			});

			marker.addTo(markersLayer);
		}

		/** Fit map to show all markers with padding. */
		if (bounds.length > 0) {
			map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 });
		}
	}

	/**
	 * Re-render markers when bucket data changes (e.g. time window toggle).
	 */
	/** Re-render markers when bucket data changes (e.g. time window toggle). */
	run(() => {
		if (map && markersLayer && buckets) {
			import('leaflet').then((L) => renderMarkers(L));
		}
	});

	onMount(() => {
		initMap();
	});

	onDestroy(() => {
		if (themeObserver) {
			themeObserver.disconnect();
			themeObserver = null;
		}
		/** Destroy the Leaflet map instance to free resources. */
		if (map) {
			map.remove();
			map = null;
		}
	});
</script>

<div class="relative w-full overflow-hidden rounded-lg" style="height: 380px;">
	<div bind:this={mapContainer} class="absolute inset-0 z-0"></div>

	<!-- Legend overlay -->
	{#if totalSessions > 0}
		<div
			class="pointer-events-none absolute right-3 top-3 z-[1000] rounded-lg bg-white/90 px-3 py-2 text-xs text-gray-600 shadow-sm dark:bg-gray-800/90 dark:text-gray-300"
		>
			<span class="font-semibold">{totalSessions.toLocaleString()}</span> {$t("sessions")} &middot;
			<span class="font-semibold">{buckets.length}</span>
			{buckets.length === 1 ? $t('region') : $t('regions')}
		</div>
	{/if}
</div>
